// ACA-266 §5.1: extracted verbatim in behaviour from page/Onboarding.jsx:453 and :464-477, the
// paid-acquisition beacons that fire once a ghost converts to a real account. This is deliberately
// the only place they exist so Phase 5 can point the legacy inline code here instead of keeping a
// second copy: page/Onboarding.jsx is out of scope for this ticket (it belongs to the flip).
//
// No React. Defensive about window.gtag / Artcloud.fullstory the same way the legacy code already
// is - `if (window && window.gtag)` there, an optional-chained read here, not a stricter guard that
// would just be a different opinion about the same missing global.

const ARTIST_CONVERSION_TAG = "AW-988451379/Swi2COyot6MYELOkqtcD";
const DEFAULT_CONVERSION_TAG = "AW-988451379/dX6oCO-ot6MYELOkqtcD";

export type ConvertedAccountType = "Artist" | "Collector" | "Gallery" | "Jewelry" | string;

type GtagFn = (...args: unknown[]) => void;

interface AnalyticsWindow {
  gtag?: GtagFn;
  Artcloud?: {
    fullstory?: {
      event?: (eventName: string, eventProperties: Record<string, unknown>) => void;
    };
  };
}

function analyticsWindow(): AnalyticsWindow | undefined {
  return typeof window === "undefined"
    ? undefined
    : (window as unknown as AnalyticsWindow);
}

/** Onboarding.jsx:453 - fires once, right after a ghost account converts. */
export function emitAccountConverted(): void {
  analyticsWindow()?.Artcloud?.fullstory?.event?.("OB: Account Converted", {});
}

/**
 * Onboarding.jsx:464-477. Fires the Google Ads conversion tag for the account type just created
 * and calls onComplete once the tag has fired - passed as gtag's own event_callback, never invoked
 * alongside it. The redirect that follows has to wait for that callback, or the beacon race-loses
 * against navigation and paid-acquisition attribution comes up empty.
 *
 * A Collector gets no tag (the legacy switch has no case for one - the gallery tag would
 * misattribute them) and calls onComplete immediately, same as when gtag itself is unavailable.
 */
export function trackAccountConversion(
  accountType: ConvertedAccountType,
  onComplete: () => void,
): void {
  const w = analyticsWindow();
  if (typeof w?.gtag !== "function") {
    onComplete();
    return;
  }
  const gtag = w.gtag;
  switch (accountType) {
    case "Artist":
      gtag("event", "conversion", {
        send_to: ARTIST_CONVERSION_TAG,
        event_callback: onComplete,
      });
      break;
    case "Collector":
      onComplete();
      break;
    default:
      gtag("event", "conversion", {
        send_to: DEFAULT_CONVERSION_TAG,
        event_callback: onComplete,
      });
      break;
  }
}
